import React, { useEffect, useRef, useState } from 'react';
import axios from '../axiosConfig';
import './AccountModal.css';

export default function AccountModal({ username, onSaved, onClose }) {
    const dialogRef = useRef(null);
    const [name, setName] = useState(username);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const previouslyFocused = document.activeElement;
        dialogRef.current.showModal();
        return () => previouslyFocused?.focus();
    }, []);

    const save = async (event) => {
        event.preventDefault();
        if (saving) return;
        setError('');
        setSuccess('');
        const changingPassword = Boolean(currentPassword || newPassword || confirmation);
        if (name.trim().length < 3) return setError('Name must be at least 3 characters.');
        if (changingPassword && !currentPassword) return setError('Enter your current password.');
        if (changingPassword && newPassword.length < 6) return setError('New password must be at least 6 characters.');
        if (newPassword !== confirmation) return setError('New passwords do not match.');
        setSaving(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/update_profile.php`, {
                username: name.trim(),
                ...(changingPassword ? { current_password: currentPassword, new_password: newPassword, confirm_password: confirmation } : {})
            });
            if (!data.success) throw new Error(data.error || 'Unable to save account changes.');
            localStorage.setItem('username', data.username);
            onSaved(data.username);
            setName(data.username);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmation('');
            setSuccess('Your account has been updated.');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Unable to save account changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <dialog ref={dialogRef} className="account-dialog" aria-labelledby="account-title" onCancel={(event) => { event.preventDefault(); if (!saving) onClose(); }}>
            <form onSubmit={save}>
                <div className="account-heading">
                    <h2 id="account-title">My account</h2>
                    <button type="button" aria-label="Close account settings" disabled={saving} onClick={onClose}>×</button>
                </div>
                <fieldset disabled={saving}>
                    <label htmlFor="account-name">Name / username</label>
                    <input id="account-name" autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={100} aria-describedby="account-name-hint" />
                    <p id="account-name-hint">This is also your login username. Use your updated name the next time you sign in, or continue using your email.</p>
                    <h3>Change password</h3>
                    <p>Leave these fields blank to keep your current password.</p>
                    <label htmlFor="account-current-password">Current password</label>
                    <input id="account-current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                    <label htmlFor="account-new-password">New password</label>
                    <input id="account-new-password" type="password" autoComplete="new-password" minLength={6} maxLength={72} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} aria-describedby="account-password-hint" />
                    <p id="account-password-hint">Use at least 6 characters.</p>
                    <label htmlFor="account-confirm-password">Confirm new password</label>
                    <input id="account-confirm-password" type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
                </fieldset>
                {error && <p className="account-error" role="alert">{error}</p>}
                {success && <p className="account-success" role="status">{success}</p>}
                <div className="account-actions">
                    <button type="button" disabled={saving} onClick={onClose}>Close</button>
                    <button type="submit" className="account-save" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
            </form>
        </dialog>
    );
}
